import React from 'react'
import { motion } from 'framer-motion'
import heroImage from '../assets/images/hero-image.jpeg'
import { useRouter } from '../hooks/use-router'
import ExpandButton from '../components/shared/ExpandButton'

// Reusable fade-in variant for elements
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
}

const PersonalConsultingPage = () => {
  const router = useRouter()
  return (
    <section className='w-full pb-40'>
      <div
        className='relative bg-cover bg-center h-[500px]'
        style={{
          backgroundImage: `linear-gradient(rgba(23, 37, 84, .4), rgba(23, 37, 84, .4)),url(${heroImage})`,
        }}
      >
        <div className='flex flex-col items-center justify-center h-full text-center text-white w-full'>
          <motion.h1
            initial={{
              y: 15,
              opacity: 0,
            }}
            animate={{
              y: 0,
              opacity: 1,
            }}
            transition={{
              duration: 0.75,
              delay: 0.25,
              ease: 'easeInOut',
            }}
            className='text-4xl font-light mb-2 w-full'
          >
            Personal consulting
          </motion.h1>
          <div className='w-full flex justify-center my-4'>
            <motion.div
              initial={{ width: '0%' }}
              whileInView={{ width: 200 }}
              transition={{ duration: 0.75, ease: 'easeInOut', delay: 0.5 }}
              className='w-64 h-[2px] origin-center bg-white'
            />
          </div>

          <motion.p
            initial={{
              y: 15,
              opacity: 0,
            }}
            animate={{
              y: 0,
              opacity: 1,
            }}
            transition={{
              duration: 0.75,
              delay: 0.6,
              ease: 'easeInOut',
            }}
            className='text-lg tracking-wide'
          >
            “Smart Yacht Ownership – Your Ultimate Guide to Profitability”
          </motion.p>
        </div>
      </div>
      <main className='mx-auto max-w-5xl px-4 py-16 text-center'>
        {/* Page Title */}
        {/* <motion.h1
          className='text-3xl md:text-4xl font-bold mb-6'
          initial='hidden'
          whileInView='visible'
          variants={fadeInUp}
          transition={{ duration: 0.6 }}
          viewport={{ once: true, amount: 0.2 }}
        >
          Smart Yacht Ownership – Your Ultimate Guide to Profitability
        </motion.h1> */}

        {/* Subtitle */}
        <motion.h2
          className='text-brandPrimary text-xl md:text-2xl font-semibold mb-4 uppercase tracking-wide'
          initial='hidden'
          whileInView='visible'
          variants={fadeInUp}
          transition={{ duration: 0.6, delay: 0.1 }}
          viewport={{ once: true, amount: 0.2 }}
        >
          OWNING A YACHT SHOULD BE A SMART INVESTMENT, NOT JUST A LUXURY
        </motion.h2>

        {/* Intro Paragraph */}
        <motion.p
          className='text-center text-neutral-500 mt-8 mb-12 leading-relaxed'
          initial='hidden'
          whileInView='visible'
          variants={fadeInUp}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true, amount: 0.2 }}
        >
          Most people think yachts are just expensive toys. But what if your
          yacht could generate passive income, cover its operating costs, and
          even turn a profit?
          <br />
          <br />
          At Ethos Yachting, we don’t just help you buy a yacht—we strategically
          position your asset for maximum financial return. Whether you’re
          looking to invest in a yacht for charter, optimize operational costs,
          or plan your long-term exit strategy, our data-driven approach ensures
          your investment works for you.
        </motion.p>

        {/* Consulting Services Heading */}
        <motion.h2
          className='text-xl md:text-2xl font-semibold mb-6 uppercase tracking-wide text-brandPrimary'
          initial='hidden'
          whileInView='visible'
          variants={fadeInUp}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true, amount: 0.2 }}
        >
          OUR CONSULTING SERVICES: MAKING YACHT OWNERSHIP PROFITABLE
        </motion.h2>

        {/* 1. YACHT INVESTMENT CONSULTING */}
        <SectionBlock
          title='YACHT INVESTMENT CONSULTING'
          bullets={[
            'Which yachts generate the best charter returns?',
            'What are the hidden costs of ownership, and how do you reduce them?',
            'How can you structure your purchase to be tax-efficient and ROI-focused?',
          ]}
          paragraph='We provide market analysis, revenue projections, and personalized investment roadmaps so you can own a yacht that pays for itself.'
        />

        {/* 2. YACHT PURCHASE ADVISORY */}
        <SectionBlock
          title='YACHT PURCHASE ADVISORY'
          bullets={[
            'We identify the best yacht deals before they hit the open market.',
            'We negotiate aggressively to get you the lowest possible price.',
            'We conduct in-depth inspections, valuations, and financial forecasts.',
          ]}
          paragraph='Our clients avoid costly mistakes and purchase with full confidence, knowing they are making the right decision.'
        />

        {/* 3. TAX, FLAG, AND LEGAL STRUCTURING */}
        <SectionBlock
          title='TAX, FLAG, AND LEGAL STRUCTURING'
          bullets={[
            'Choosing the right flag can save you millions in taxes.',
            'We navigate VAT, ownership structures, and international compliance.',
            'Our expertise ensures seamless yacht operation worldwide.',
          ]}
          paragraph='Many buyers overpay in taxes without realizing they have better options—we make sure you don’t.'
        />

        {/* 4. OPERATIONAL COST MANAGEMENT & CHARTER STRATEGY */}
        <SectionBlock
          title='OPERATIONAL COST MANAGEMENT & CHARTER STRATEGY'
          bullets={[
            'Cut unnecessary expenses while maintaining world-class service.',
            'We help owners turn their yacht into a profitable charter business.',
            'Monthly budget optimization ensures maximum efficiency & longevity.',
          ]}
          paragraph='With the right management approach, your yacht can generate passive income instead of draining cash.'
        />

        {/* 5. FRACTIONAL OWNERSHIP & YACHT SYNDICATION */}
        <SectionBlock
          title='FRACTIONAL OWNERSHIP & YACHT SYNDICATION'
          bullets={[
            'Want a yacht but don’t want 100% of the financial burden? Shared ownership is the future.',
            'We structure co-ownership deals that maximize usage and minimize costs.',
            'Our financial planning ensures a clear exit strategy for every investor.',
          ]}
          paragraph='This model is ideal for entrepreneurs, real estate investors, and luxury enthusiasts who want to enjoy yacht ownership with lower financial exposure.'
        />

        {/* Why Choose Ethos */}
        <motion.h2
          className='text-brandPrimary text-xl md:text-2xl font-semibold mb-4 uppercase tracking-wide mt-12'
          initial='hidden'
          whileInView='visible'
          variants={fadeInUp}
          transition={{ duration: 0.6, delay: 0.1 }}
          viewport={{ once: true, amount: 0.2 }}
        >
          WHY CHOOSE ETHOS YACHTING?
        </motion.h2>
        <motion.div
          className='text-left mb-8'
          initial='hidden'
          whileInView='visible'
          variants={fadeInUp}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true, amount: 0.2 }}
        >
          <BulletPoint text='We Think Like Investors, Not Salesmen → We tell you what’s profitable, not just what’s expensive.' />
          <BulletPoint text='We Offer Full Lifecycle Support → From purchase to chartering to eventual resale, we are your long-term partner.' />
          <BulletPoint text='We Use Data, Not Guesswork → Every recommendation is backed by real charter and ownership insights.' />
        </motion.div>

        <motion.p
          className='text-brandPrimary mb-8 leading-relaxed'
          initial='hidden'
          whileInView='visible'
          variants={fadeInUp}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true, amount: 0.2 }}
        >
          Owning a yacht doesn’t have to be a liability. Make it a profitable,
          strategically managed asset with Ethos Yachting.
        </motion.p>

        {/* Final CTA */}
        <motion.h2
          className='text-brandPrimary text-xl md:text-2xl font-semibold mb-4 uppercase tracking-wide'
          initial='hidden'
          whileInView='visible'
          variants={fadeInUp}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true, amount: 0.2 }}
        >
          READY TO MAKE A SMARTER YACHT INVESTMENT?
        </motion.h2>
        <motion.p
          className='text-brandPrimary mb-6 leading-relaxed'
          initial='hidden'
          whileInView='visible'
          variants={fadeInUp}
          transition={{ duration: 0.6, delay: 0.5 }}
          viewport={{ once: true, amount: 0.2 }}
        >
          🚀 Schedule a Consultation Today and start your journey toward
          intelligent, profitable yacht ownership.
        </motion.p>
        <motion.div
          className='flex justify-center'
          initial='hidden'
          whileInView='visible'
          variants={fadeInUp}
          transition={{ duration: 0.6, delay: 0.6 }}
          viewport={{ once: true, amount: 0.2 }}
        >
          <ExpandButton
            buttonText='Book A Call'
            onClick={() => router.push('/contact')}
          />
        </motion.div>
      </main>
    </section>
  )
}

export default PersonalConsultingPage

/* Reusable Section Component */
function SectionBlock({ title, bullets, paragraph }) {
  return (
    <motion.section
      className='text-left my-12'
      initial='hidden'
      whileInView='visible'
      variants={fadeInUp}
      transition={{ duration: 0.6, delay: 0.1 }}
      viewport={{ once: true, amount: 0.2 }}
    >
      <h3 className='text-lg md:text-xl font-semibold mb-3 uppercase tracking-wide text-brandPrimary'>
        {title}
      </h3>
      <ul className='mb-4'>
        {bullets.map((bullet, index) => (
          <li key={index} className='mb-2'>
            <BulletPoint text={bullet} />
          </li>
        ))}
      </ul>
      <p className='mt-8 text-brandPrimary leading-relaxed text-center'>
        {paragraph}
      </p>
    </motion.section>
  )
}

/* Reusable Check Mark Bullet */
function BulletPoint({ text }) {
  return (
    <div className='flex items-start'>
      <CheckIcon />
      <span className='ml-2 text-neutral-500'>{text}</span>
    </div>
  )
}

/* Inline SVG Check Mark Icon */
function CheckIcon() {
  return (
    <svg
      className='flex-shrink-0 w-5 h-5 text-brandPrimary mt-0.5'
      fill='none'
      stroke='currentColor'
      strokeWidth={2}
      viewBox='0 0 24 24'
    >
      <path strokeLinecap='round' strokeLinejoin='round' d='M5 13l4 4L19 7' />
    </svg>
  )
}
